import type { OutgoingHttpHeaders } from 'http';

import { ResolverTag } from '@ps/web-core-enums';
import { getWindow } from '@ps/web-core-utils';
import type { Message } from '@stomp/stompjs';
import jwtDecode from 'jwt-decode';
import type SockJS from 'sockjs-client';

import store, { dispatch, select } from '@swf/core/store';

export enum ConnectionType {
  WMT,
  Rewards,
  RewardsKong
}

export interface APIConfig {
  requestDestination: string;
  getConnectionHeaders?(): object | undefined;
  getSubscriptionHeaders?(): Message['headers'] | undefined;
  responseChannel: string;
  requestTimeout: number;
  sockJSOptions?: SockJS.Options & {
    transportOptions?: { websocket: { headers: OutgoingHttpHeaders } };
  };
  type: ConnectionType;
  url: string;
  getReconnect(currentDelay: number, disconnectReason: string): number;
}

export enum WMTDisconnectReason {
  BadFormat = 'BF',
  InvalidSubscriptionDestination = 'ID',
  UnknownOrigin = 'UO',
  InvalidMessageType = 'MT',
  TokenRequired = 'TR',
  UserChangeNotAllowed = 'UC',
  BadSignature = 'BS',
  InvalidToken = 'IT',
  InvalidSessionId = 'IS',
  VelocityControl = 'VC',
  MaxSubscriptionExceeded = 'MS',
  AllTransportsFailed = 'All transports failed'
}

const WMT_RECONNECT = 1000;

export const WMTConfig = (): APIConfig => {
  const { getState, dispatch } = store;
  const { requestTimeout, wsSockJsTimeout } = select.config.get(getState());

  const getHeaders = () => {
    const authorization = select.session.getWMTAuthorization(getState());
    return authorization ? { authorization } : undefined;
  };

  return {
    requestDestination: '/service/',
    requestTimeout: requestTimeout ? requestTimeout : 20000,
    responseChannel: '/user/queue/messages',
    type: ConnectionType.WMT,
    url: '/api/v0/websocket',
    sockJSOptions: { transports: ['websocket'], timeout: wsSockJsTimeout ?? 5000 },
    getConnectionHeaders: getHeaders,
    getSubscriptionHeaders: getHeaders,
    getReconnect: (currentDelay, reason: WMTDisconnectReason) => {
      if (reason === WMTDisconnectReason.InvalidToken) {
        dispatch.auth.logout({ implicit: true }).then(() => getWindow()?.starsweb?.auth.login());
      }
      if (!currentDelay) {
        return reason === WMTDisconnectReason.VelocityControl ? WMT_RECONNECT : 1;
      }
      return currentDelay === 1 ? WMT_RECONNECT : currentDelay * 2;
    }
  };
};

const rewardsConfig = async (): Promise<APIConfig> => {
  const { dispatch, getState } = store;
  await dispatch.resolver.fetchTag({ tag: ResolverTag.StarsRewardsWidgetService });
  const { url } = select.resolver.getTag(getState(), ResolverTag.StarsRewardsWidgetService);

  return {
    // @TODO infer reconnect from disconnect reason
    getReconnect: () => 5000,
    getConnectionHeaders: () => {
      const { token, signature, userWebId } = select.session.getLegacySession(getState());
      return { WBID: userWebId, SBTK: token, SBSG: signature };
    },
    requestDestination: '/app/starsrewards/api',
    requestTimeout: 10000,
    responseChannel: `/topic/api/${select.session.getLegacySession(getState()).token}`,
    type: ConnectionType.Rewards,
    url
  };
};

const widgetServiceKongConfig = async (): Promise<APIConfig> => {
  const { getState } = store;
  const { idpToken } = select.session.get(getState());
  const jwtPayload: { sub: string } = jwtDecode(idpToken as string);
  await dispatch.resolver.fetchTag({ tag: ResolverTag.StarsRewardsWidgetService });
  const { url } = select.resolver.getTag(getState(), ResolverTag.StarsRewardsWidgetService);

  const getWidgetServiceKongHeaders = () => select.rewardsLegacy.getKongHeaders(getState());

  return {
    url,
    getReconnect: () => 5000,
    getConnectionHeaders: getWidgetServiceKongHeaders,
    getSubscriptionHeaders: getWidgetServiceKongHeaders,
    requestDestination: '/app/starsrewards/api',
    requestTimeout: 10000,
    responseChannel: `/topic/api/${btoa(jwtPayload?.sub)}`,
    type: ConnectionType.RewardsKong
  };
};

export const getConfig = async (type: ConnectionType) => {
  switch (type) {
    case ConnectionType.Rewards:
      return rewardsConfig();
    case ConnectionType.WMT:
      return WMTConfig();
    case ConnectionType.RewardsKong:
      return widgetServiceKongConfig();
  }
};
